<template>
  <div 
    class="cp-popover" 
    @click.stop
  >
    <div
      ref="svBox"
      class="cp-sv"
      :style="{ backgroundColor: hueColor }"
      @mousedown="startDragSV"
    >
      <div class="cp-sv-white" />
      <div class="cp-sv-black" />
      <div
        class="cp-sv-handle"
        :style="{ left: sv.s * 100 + '%', top: (1 - sv.v) * 100 + '%' }"
      />
    </div>

    <div class="cp-controls">
      <button
        type="button"
        class="cp-eyedropper"
        title="Pick color from screen"
        @click="pickFromScreen"
      >
        <svg 
          width="18" 
          height="18" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        >
          <path d="M2 22l1-4 9-9" />
          <path d="M17.5 3.5a2.121 2.121 0 0 1 3 3L18 9l-3-3z" />
          <path d="M15 6l3 3" />
        </svg>
      </button>

      <div 
        class="cp-swatch" 
        :style="{ backgroundColor: hexValue }" 
      />

      <div
        ref="hueSlider" 
        class="cp-hue-slider" 
        @mousedown="startDragHue"
      >
        <div 
          class="cp-hue-handle" 
          :style="{ left: (hue / 360) * 100 + '%' }"
        />
      </div>
    </div>

    <div class="cp-rgb-row">
      <div class="cp-rgb-field">
        <input
          v-model.number="rgb.r"
          type="number"
          min="0"
          max="255"
          @change="updateFromRGB"
        >
        <label>R</label>
      </div>
      <div class="cp-rgb-field">
        <input
          v-model.number="rgb.g"
          type="number"
          min="0"
          max="255"
          @change="updateFromRGB"
        >
        <label>G</label>
      </div>
      <div class="cp-rgb-field">
        <input
          v-model.number="rgb.b"
          type="number"
          min="0"
          max="255"
          @change="updateFromRGB"
        >
        <label>B</label>
      </div>
      <div class="cp-rgb-stepper">
        <button 
          type="button" 
          @click="nudge(1)"
        >
          ▲
        </button>
        <button 
          type="button" 
          @click="nudge(-1)"
        >
          ▼
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_COLOR = '#0F172A';

function clamp(value, min, max) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return min;
  }

  return Math.min(max, Math.max(min, number));
}

function normalizeHex(value) {
  const hex = String(value || '').trim();
  const shortMatch = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);

  if (shortMatch) {
    return `#${shortMatch[1]}${shortMatch[1]}${shortMatch[2]}${shortMatch[2]}${shortMatch[3]}${shortMatch[3]}`.toUpperCase();
  }

  const fullMatch = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!fullMatch) {
    return null;
  }

  return `#${fullMatch[1]}${fullMatch[2]}${fullMatch[3]}`.toUpperCase();
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex);

  if (!normalized) {
    return null;
  }

  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function rgbToHex(r, g, b) {
  const toHex = (value) => {
    return Math.round(clamp(value, 0, 255))
      .toString(16)
      .padStart(2, '0');
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function rgbToHsv(red, green, blue) {
  const r = clamp(red, 0, 255) / 255;
  const g = clamp(green, 0, 255) / 255;
  const b = clamp(blue, 0, 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const difference = max - min;

  let hue = 0;

  if (difference !== 0) {
    switch (max) {
      case r:
        hue = ((g - b) / difference) % 6;
        break;
      case g:
        hue = (b - r) / difference + 2;
        break;
      default:
        hue = (r - g) / difference + 4;
        break;
    }

    hue *= 60;

    if (hue < 0) {
      hue += 360;
    }
  }

  return {
    h: hue,
    s: max === 0 ? 0 : difference / max,
    v: max,
  };
}

function hsvToRgb(hue, saturation, value) {
  const h = ((Number(hue) || 0) % 360 + 360) % 360;
  const s = clamp(saturation, 0, 1);
  const v = clamp(value, 0, 1);

  const chroma = v * s;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const match = v - chroma;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    [r, g, b] = [chroma, x, 0];
  } else if (h < 120) {
    [r, g, b] = [x, chroma, 0];
  } else if (h < 180) {
    [r, g, b] = [0, chroma, x];
  } else if (h < 240) {
    [r, g, b] = [0, x, chroma];
  } else if (h < 300) {
    [r, g, b] = [x, 0, chroma];
  } else {
    [r, g, b] = [chroma, 0, x];
  }

  return {
    r: Math.round((r + match) * 255),
    g: Math.round((g + match) * 255),
    b: Math.round((b + match) * 255),
  };
}

export default {
  name: 'ColorPickerPopover',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: String,
      default: DEFAULT_COLOR,
    },
  },
  data() {
    const rgb = hexToRgb(this.value) || hexToRgb(DEFAULT_COLOR);
    const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

    return {
      rgb,
      hue: hsv.h,
      sv: {
        s: hsv.s,
        v: hsv.v,
      },
      draggingSV: false,
      draggingHue: false,
    };
  },
  computed: {
    hueColor() {
      return `hsl(${this.hue}, 100%, 50%)`;
    },

    hexValue() {
      return rgbToHex(this.rgb.r, this.rgb.g, this.rgb.b);
    },
  },
  watch: {
    value(nextValue) {
      const normalized = normalizeHex(nextValue);

      if (!normalized || normalized === this.hexValue) {
        return;
      }

      this.setFromHex(normalized);
    },
  },
  mounted() {
    window.addEventListener('mousemove', this.onDrag);
    window.addEventListener('mouseup', this.stopDrag);
  },
  beforeDestroy() {
    this.stopDrag();
    window.removeEventListener('mousemove', this.onDrag);
    window.removeEventListener('mouseup', this.stopDrag);
  },
  methods: {
    startDragSV(event) {
      event.preventDefault();
      this.draggingSV = true;
      this.updateSVFromEvent(event);
    },

    startDragHue(event) {
      event.preventDefault();
      this.draggingHue = true;
      this.updateHueFromEvent(event);
    },

    onDrag(event) {
      if (this.draggingSV) {
        this.updateSVFromEvent(event);
      }

      if (this.draggingHue) {
        this.updateHueFromEvent(event);
      }
    },

    stopDrag() {
      this.draggingSV = false;
      this.draggingHue = false;
    },

    updateSVFromEvent(event) {
      const element = this.$refs.svBox;

      if (!element) {
        return;
      }

      const box = element.getBoundingClientRect();

      if (!box.width || !box.height) {
        return;
      }

      const x = clamp((event.clientX - box.left) / box.width, 0, 1);
      const y = clamp((event.clientY - box.top) / box.height, 0, 1);

      this.sv = {
        s: x,
        v: 1 - y,
      };

      this.syncFromHSV();
    },

    updateHueFromEvent(event) {
      const element = this.$refs.hueSlider;

      if (!element) {
        return;
      }

      const slider = element.getBoundingClientRect();

      if (!slider.width) {
        return;
      }

      const x = clamp((event.clientX - slider.left) / slider.width, 0, 1);

      this.hue = x === 1 ? 359.999 : x * 360;
      this.syncFromHSV();
    },

    syncFromHSV() {
      this.rgb = hsvToRgb(this.hue, this.sv.s, this.sv.v);
      this.emitValue();
    },

    updateFromRGB() {
      this.rgb = {
        r: Math.round(clamp(this.rgb.r, 0, 255)),
        g: Math.round(clamp(this.rgb.g, 0, 255)),
        b: Math.round(clamp(this.rgb.b, 0, 255)),
      };

      const hsv = rgbToHsv(this.rgb.r, this.rgb.g, this.rgb.b);

      this.hue = hsv.h;
      this.sv = {
        s: hsv.s,
        v: hsv.v,
      };

      this.emitValue();
    },

    nudge(direction) {
      const hsv = rgbToHsv(this.rgb.r, this.rgb.g, this.rgb.b);

      this.sv = {
        s: hsv.s,
        v: clamp(hsv.v + direction * 0.02, 0, 1),
      };

      this.syncFromHSV();
    },

    emitValue() {
      this.$emit('input', this.hexValue);
    },

    setFromHex(value) {
      const rgb = hexToRgb(value);

      if (!rgb) {
        return;
      }

      const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

      this.rgb = rgb;
      this.hue = hsv.h;
      this.sv = {
        s: hsv.s,
        v: hsv.v,
      };
    },

    async pickFromScreen() {
      if (!window.EyeDropper) {
        alert("Your browser doesn't support the EyeDropper API.");
        return;
      }

      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();

        if (!result?.sRGBHex) {
          return;
        }

        this.setFromHex(result.sRGBHex);
        this.emitValue();
      } catch (error) {
        // The user cancelled the eyedropper.
      }
    },
  },
};
</script>

<style scoped>
.cp-popover {
  width: 260px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 14px;
  font-family: inherit;
}

/* Saturation/Value box */
.cp-sv {
  position: relative;
  width: 100%;
  height: 150px;
  border-radius: 8px;
  cursor: crosshair;
  overflow: hidden;
  user-select: none;
}
.cp-sv-white {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0));
}
.cp-sv-black {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, #000, rgba(0, 0, 0, 0));
}
.cp-sv-handle {
  position: absolute;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.4);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

/* Controls row: eyedropper, swatch, hue slider */
.cp-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.cp-eyedropper {
  flex: none;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
}
.cp-eyedropper:hover {
  background: #f3f4f6;
}
.cp-swatch {
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
}
.cp-hue-slider {
  position: relative;
  flex: 1;
  height: 10px;
  border-radius: 999px;
  cursor: pointer;
  background: linear-gradient(
    to right,
    #ff0000 0%,
    #ffff00 17%,
    #00ff00 33%,
    #00ffff 50%,
    #0000ff 67%,
    #ff00ff 83%,
    #ff0000 100%
  );
}
.cp-hue-handle {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25), 0 1px 3px rgba(0, 0, 0, 0.35);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

/* RGB inputs row */
.cp-rgb-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-top: 14px;
}
.cp-rgb-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.cp-rgb-field input {
  width: 100%;
  text-align: center;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 4px;
  font-size: 0.85rem;
  -moz-appearance: textfield;
}
.cp-rgb-field input::-webkit-outer-spin-button,
.cp-rgb-field input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.cp-rgb-field label {
  font-size: 0.7rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.cp-rgb-stepper {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 18px;
}
.cp-rgb-stepper button {
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 0.55rem;
  line-height: 1;
  cursor: pointer;
  padding: 2px;
}
.cp-rgb-stepper button:hover {
  color: #4b5563;
}
</style>